'use client';
import * as React from 'react';
import {
  ArticleCard,
  type ArticleCardProps,
} from '@/hen-ui/composites/ArticleCard';
import { ArticleRow } from '@/hen-ui/composites/ArticleRow';
import { Grid } from '@/hen-ui/composites/Grid';
import { Heading } from '@/hen-ui/atomics/Heading';
import { cn } from '@/hen-ui/utils';

export interface ArticleLayoutProps {
  /**
   * Section-level title rendered as an `<h2>` above the two-column grid.
   * When omitted, no heading is rendered.
   */
  title?: string;
  /**
   * The featured article rendered as a full-bleed overlay card on the left column.
   */
  featuredArticle?: ArticleCardProps;
  /**
   * Supporting articles rendered in a vertical stacked list on the right column.
   */
  articles?: ArticleCardProps[];
  /**
   * Border variant for the right-column article cards.
   * @default 'padding'
   */
  border?: ArticleCardProps['border'];
  /**
   * Hover style for the right-column article cards.
   * @default 'shadow'
   */
  hoverStyle?: ArticleCardProps['hoverStyle'];
  /**
   * Whether to show the thumbnail image on right-column cards.
   * Set to `false` to render text-only compact cards.
   * @default true
   */
  showRowImages?: boolean;
  /**
   * URL for a CTA link rendered below the right-column article list.
   * When provided, pagination arrows are suppressed even if there are more
   * than 3 articles.
   */
  link?: string;
  /** Label text for the CTA link. */
  linkLabel?: string;
  /** Optional className for the outer grid container */
  className?: string;
}

/**
 * ArticleLayout component following HEN Design System standards.
 *
 * A two-column section composite: a large overlay `ArticleCard` on the left and
 * a vertical stacked `ArticleRow` on the right. Collapses to a single column on
 * mobile. Designed for dynamic CMS-driven article sections.
 *
 * @example
 * <ArticleLayout
 *   featuredArticle={{ image: '/hero.jpg', title: 'Featured Article', href: '/featured' }}
 *   articles={[
 *     { image: '/img1.jpg', title: 'Article 2', href: '/article-2' },
 *     { image: '/img2.jpg', title: 'Article 3', href: '/article-3' },
 *     { image: '/img3.jpg', title: 'Article 4', href: '/article-4' },
 *   ]}
 * />
 */
export const ArticleLayout = React.forwardRef<
  HTMLDivElement,
  ArticleLayoutProps
>(
  (
    {
      title,
      featuredArticle,
      articles = [],
      border = 'padding',
      hoverStyle = 'shadow',
      showRowImages = true,
      link,
      linkLabel,
      className,
    },
    ref,
  ) => {
    const rightColumnArticles = articles.map((article) => ({
      ...article,
      size: 'compact' as const,
      border,
      hoverStyle,
      showImage: showRowImages,
    }));

    return (
      <div ref={ref} className='flex flex-col gap-xl'>
        {title && <Heading variant='h2'>{title}</Heading>}
        <Grid columns={1} layout='feature' className={cn('gap-3xl', className)}>
          {/* Left column — featured overlay card */}
          <div className='flex flex-col'>
            {featuredArticle ? (
              <ArticleCard {...featuredArticle} layout='overlay' size='lg' />
            ) : null}
          </div>

          {/* Right column — vertical article list */}
          <div className='flex flex-col h-full'>
            <ArticleRow
              variant='vertical'
              articles={rightColumnArticles}
              link={link}
              linkLabel={linkLabel}
              className='h-full'
            />
          </div>
        </Grid>
      </div>
    );
  },
);

ArticleLayout.displayName = 'ArticleLayout';
